import {
	BillFromRef,
	Contact,
	EnabledInvoiceIntegrations,
	fetchEnabledIntegrationContacts,
	ImportBillTo,
	resolveImportBillTos,
} from '../../forecast-app/admin-tab/finance-page/InvoiceIntegrationsUtils';
import {MODAL_TYPE, showModal} from '../../forecast-app/shared/components/modals/generic_modal_conductor';

/**
 * The contacts behind the import list, with the instance id, address and tax id the
 * unimported endpoints leave out. Never rejects: a failed fetch must not stop an import, it only
 * means those clients arrive without a Bill To (and the user is told).
 */
export const loadImportContacts = (
	billFroms: readonly BillFromRef[] | null | undefined,
	enabled: EnabledInvoiceIntegrations
): Promise<Contact[]> => (billFroms ? fetchEnabledIntegrationContacts(enabled).catch(() => []) : Promise.resolve([]));

/** The billTos to send with the import. With invoicing_pathways off (billFroms null) there is no billTos key at all. */
export const prepareImportBillTos = async (
	billFroms: readonly BillFromRef[] | null | undefined,
	integration: string,
	selections: Array<string | number | {id: string | number; integrationInstanceId?: string | null}>,
	contacts: Promise<Contact[]>
): Promise<{billTos?: ImportBillTo[]; skippedCount: number}> => {
	if (!billFroms) {
		return {skippedCount: 0};
	}
	const normalised = selections.map(selection =>
		typeof selection === 'object'
			? {id: String(selection.id), integrationInstanceId: selection.integrationInstanceId}
			: {id: String(selection)}
	);
	return resolveImportBillTos(normalised, integration, await contacts, billFroms);
};

/**
 * Every import modal reloads the page on success. With clients left unlinked, the reload waits until
 * the warning is closed so the message is not wiped out before it can be read. The conductor's own
 * closeModal pops the modal and runs its onCloseModal, and OK, X, Escape and popstate all end there
 * (OK and X via GenericModal's props.closeModal), so onCloseModal runs exactly once however it closes.
 */
export const finishClientImport = (
	skippedCount: number,
	unlinkedBillToCount: number | null | undefined,
	formatMessage: (descriptor: {id: string}, values?: Record<string, unknown>) => string
): void => {
	const unlinked = skippedCount + (unlinkedBillToCount || 0);
	if (unlinked === 0) {
		window.location.reload();
		return;
	}
	showModal({
		type: MODAL_TYPE.WARNING,
		message: formatMessage({id: 'clients.import.bill_to_unlinked_warning'}, {count: unlinked}),
		buttons: [{text: formatMessage({id: 'common.ok'})}],
		onCloseModal: () => window.location.reload(),
	});
};
